// Redux/Slices/Pages/Client/BusinessListSlice.js
// Manages: nearby business list for client home/search page
// BZV2 backend: GET /api/v2/discovery/search
// Renamed from: Services.js (clientServices)

import { createSlice } from '@reduxjs/toolkit';
import { plainAxios } from '../../../../Common/Interceptors/AxiosConfig';

const CACHE_DURATION = 5 * 60 * 1000;

// ── Session Storage Helpers ──────────────────────────────────────────────────
const SESSION_KEY = 'bz_businesses';

const loadPersistedState = () => {
  try {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.timestamp && Date.now() - parsed.timestamp < CACHE_DURATION) {
        return {
          isLoading: false,
          errors: null,
          allBusinesses: parsed.allBusinesses || [],
          displayedBusinesses: parsed.displayedBusinesses || [],
          selectedServiceGroupId: parsed.selectedServiceGroupId || 'all',
          searchQuery: '',
          businessDetails: null,
          businessDetailsLastFetchedAt: null,
          lastFetchedAt: parsed.timestamp,
          hasMore: parsed.hasMore ?? true,
          currentPage: parsed.currentPage || 1,
          lastFetchedCoords: parsed.lastFetchedCoords || null,
        };
      }
    }
  } catch { /* ignore */ }

  return {
    isLoading: false,
    errors: null,
    allBusinesses: [],
    displayedBusinesses: [],
    selectedServiceGroupId: 'all',  // renamed from selectedCategoryId
    searchQuery: '',
    businessDetails: null,
    businessDetailsLastFetchedAt: null,
    lastFetchedAt: null,
    hasMore: true,
    currentPage: 1,
    lastFetchedCoords: null,
  };
};

// ── Data Sanitization ─────────────────────────────────────────────────────────
const maskSensitiveData = (businesses) => {
  if (!businesses || !Array.isArray(businesses)) return [];
  return businesses.map(b => ({
    _id: b._id,
    id: b.id,
    name: b.name,
    description: Array.isArray(b.description) ? b.description.slice(0, 1) : String(b.description).substring(0, 100), // truncate
    city: b.city,
    location: b.location ? { coordinates: b.location.coordinates } : null,
    rating: b.rating,
    tags: b.tags,
    serviceGroups: b.serviceGroups?.map(st => ({ _id: st._id, id: st.id, name: st.name })),
    // Omitting full provider details, contact info, owner IDs
    coverImage: b.coverImage,
  }));
};

const saveToSession = (state) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({
      allBusinesses: maskSensitiveData(state.allBusinesses),
      displayedBusinesses: maskSensitiveData(state.displayedBusinesses),
      selectedServiceGroupId: state.selectedServiceGroupId,

      hasMore: state.hasMore,
      currentPage: state.currentPage,
      lastFetchedCoords: state.lastFetchedCoords,
      timestamp: Date.now(),
    }));
  } catch { /* ignore quota errors */ }
};

// ── Slice ────────────────────────────────────────────────────────────────────
const businessListSlice = createSlice({
  name: 'businesses',            // renamed from clientServices
  initialState: loadPersistedState(),
  reducers: {
    setLoading: (state, action) => { state.isLoading = action.payload; },
    setErrors: (state, action) => {
      // Ensure error is always a string or null to prevent React rendering crashes (Error #31)
      state.errors = typeof action.payload === 'string' 
        ? action.payload 
        : (action.payload?.message || "An unexpected error occurred");
    },
    clearErrors: (state) => {
      state.errors = null;
    },

    setAllBusinesses: (state, action) => {
      const { businesses, hasMore, page, coords } = action.payload;
      state.allBusinesses = businesses;
      state.displayedBusinesses = businesses;
      state.hasMore = hasMore;
      state.currentPage = page;
      state.lastFetchedAt = Date.now();
      state.lastFetchedCoords = coords;
      state.errors = null;
      saveToSession(state);
    },

    appendBusinesses: (state, action) => {
      const { businesses, hasMore, page } = action.payload;
      const existingIds = new Set(state.allBusinesses.map((b) => b._id?.toString()));
      const newItems = businesses.filter((b) => !existingIds.has(b._id?.toString()));
      state.allBusinesses = [...state.allBusinesses, ...newItems];
      state.displayedBusinesses = [...state.displayedBusinesses, ...newItems];
      state.hasMore = hasMore;
      state.currentPage = page;
      state.errors = null;
      saveToSession(state);
    },

    // Filter by serviceGroup (replaces old serviceCategories filter)
    filterByServiceGroup: (state, action) => {
      const serviceGroupId = action.payload;
      state.selectedServiceGroupId = serviceGroupId;
      state.searchQuery = '';
      state.displayedBusinesses = serviceGroupId === 'all'
        ? state.allBusinesses
        : state.allBusinesses.filter((b) =>
            b.serviceGroups?.some((st) =>
              (st._id || st.id || st)?.toString() === serviceGroupId
            )
          );
      saveToSession(state);
    },

    searchBusinesses: (state, action) => {
      const query = action.payload?.toLowerCase().trim();
      if (!query) {
        state.searchQuery = '';
        state.displayedBusinesses = state.selectedServiceGroupId === 'all'
          ? state.allBusinesses
          : state.allBusinesses.filter((b) =>
              b.serviceGroups?.some((st) =>
                (st._id || st.id)?.toString() === state.selectedServiceGroupId
              )
            );
        return;
      }
      state.searchQuery = query;
      state.displayedBusinesses = state.allBusinesses.filter((b) =>
        b.name?.toLowerCase().includes(query) ||
        b.description?.toLowerCase().includes(query) ||
        b.city?.toLowerCase().includes(query) ||
        b.tags?.some((t) => t?.toLowerCase().includes(query)) ||
        b.serviceGroups?.some((st) => (st.name || '')?.toLowerCase().includes(query))
      );
    },

    clearSearch: (state) => {
      state.searchQuery = '';
      state.displayedBusinesses = state.selectedServiceGroupId === 'all'
        ? state.allBusinesses
        : state.allBusinesses.filter((b) =>
            b.serviceGroups?.some((st) =>
              (st._id || st.id)?.toString() === state.selectedServiceGroupId
            )
          );
    },

    setBusinessDetails: (state, action) => {
      state.businessDetails = action.payload;
      state.businessDetailsLastFetchedAt = Date.now();
    },
    resetBusinessDetails: (state) => {
      state.businessDetails = null;
      state.businessDetailsLastFetchedAt = null;
    },

    clearAllBusinesses: (state) => {
      state.allBusinesses = [];
      state.displayedBusinesses = [];
      state.selectedServiceGroupId = 'all';
      state.searchQuery = '';
      state.lastFetchedAt = null;
      state.hasMore = true;
      state.currentPage = 1;
      state.lastFetchedCoords = null;
      sessionStorage.removeItem(SESSION_KEY);
    },
  },
});

// ── Thunks ───────────────────────────────────────────────────────────────────

/** GET /discovery/search — paginated nearby businesses */
export const fetchNearbyBusinesses = (params = {}) => async (dispatch, getState) => {
  const { lat, lng, city, workspaceClassId, page = 1, limit = 20, reset = true } = params;
  const state = getState().businesses;

  if (reset && page === 1) {
    const coordsMatch = state.lastFetchedCoords?.lat === lat && state.lastFetchedCoords?.lng === lng;
    const cacheValid = state.lastFetchedAt && Date.now() - state.lastFetchedAt < CACHE_DURATION;
    if (state.allBusinesses.length > 0 && cacheValid && coordsMatch) return;
  }

  dispatch(setLoading(true));
  dispatch(setErrors(null));

  try {
    const queryParams = new URLSearchParams();
    if (lat != null) queryParams.append('lat', lat.toString());
    if (lng != null) queryParams.append('lng', lng.toString());
    if (city) queryParams.append('city', city);
    if (workspaceClassId) queryParams.append('workspaceClassId', workspaceClassId);
    queryParams.append('page', (page || 1).toString());
    queryParams.append('limit', (limit || 20).toString());

    const response = await plainAxios.get(`/discovery/search?${queryParams.toString()}`);
    const { success, data: searchData, message } = response?.data || {};

    if (success) {
      const businesses = searchData?.results || [];
      const pagination = searchData?.pagination;
      const hasMore = pagination?.hasMore ?? (businesses.length === limit);

      if (reset || page === 1) {
        dispatch(setAllBusinesses({ businesses, hasMore, page, coords: { lat, lng } }));
      } else {
        dispatch(appendBusinesses({ businesses, hasMore, page }));
      }
    } else {
      dispatch(setErrors(message || 'Failed to fetch businesses'));
    }
  } catch (error) {
    const msg = error?.response?.data?.message || error.message || 'Failed to fetch businesses';
    dispatch(setErrors(msg));
  } finally {
    dispatch(setLoading(false));
  }
};

/** GET /discovery/details/:businessId */
export const getBusinessDetails = (businessId) => async (dispatch, getState) => {
  const state = getState().businesses;
  const cachedId = (state.businessDetails?._id || state.businessDetails?.id)?.toString();
  const cacheValid =
    state.businessDetailsLastFetchedAt &&
    Date.now() - state.businessDetailsLastFetchedAt < CACHE_DURATION;

  if (cachedId === businessId?.toString() && cacheValid) return;

  dispatch(setLoading(true));
  dispatch(setErrors(null));

  try {
    const response = await plainAxios.get(`/discovery/details/${businessId}`);
    const { success, data, message } = response?.data || {};
    if (success) {
      dispatch(setBusinessDetails(data));
    } else {
      dispatch(setErrors(message || 'Failed to fetch business details'));
    }
  } catch (error) {
    const msg = error?.response?.data?.message || error.message || 'Failed to load business';
    dispatch(setErrors(msg));
  } finally {
    dispatch(setLoading(false));
  }
};

export const {
  setLoading, setErrors, clearErrors,
  setAllBusinesses, appendBusinesses,
  filterByServiceGroup, searchBusinesses, clearSearch,
  setBusinessDetails, resetBusinessDetails, clearAllBusinesses,
} = businessListSlice.actions;

export default businessListSlice.reducer;
